import { AgentUsage, StoredTurn } from './types';

/** The usage strip (chat-ui-spec §7, contract §8.3); the live frame and a restored turn match. */

const num = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

/** A stored turn's usage, or null (no strip) when its cost is missing or not a finite number. */
export function usageFromTurn(turn: Partial<StoredTurn>): AgentUsage | null {
  const raw = turn.cost_usd;
  if (raw === null || raw === undefined || raw === '') return null;
  const costUsd = Number(raw);
  if (!Number.isFinite(costUsd)) return null;
  return {
    model: turn.model || '-',
    effort: turn.reasoning_effort || '-',
    turns: num(turn.model_turns),
    maxTurns: num(turn.max_turns),
    inputTokens: num(turn.input_tokens),
    cachedTokens: num(turn.cached_tokens),
    outputTokens: num(turn.output_tokens),
    reasoningTokens: num(turn.reasoning_tokens),
    costUsd,
  };
}

/** `4.2k` from 1000 up, otherwise the whole number. */
export const compact = (n: number): string =>
  n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(Math.round(n));

export const formatCost = (cost: number): string =>
  cost < 0.01 ? cost.toFixed(5) : cost.toFixed(3);

export interface UsagePart {
  label: string;
  value: string;
  /** `$` sits directly against its value; every other label has a space. */
  tight: boolean;
}

export function usageParts(u: AgentUsage): UsagePart[] {
  return [
    { label: '$', value: formatCost(num(u.costUsd)), tight: true },
    { label: 'turns', value: `${num(u.turns)}/${num(u.maxTurns)}`, tight: false },
    {
      label: 'in',
      value: `${compact(num(u.inputTokens))} (${compact(num(u.cachedTokens))} cached)`,
      tight: false,
    },
    {
      label: 'out',
      value: `${compact(num(u.outputTokens))} (${compact(num(u.reasoningTokens))} reasoning)`,
      tight: false,
    },
    { label: 'model', value: u.model || '-', tight: false },
    { label: 'thinking', value: u.effort || '-', tight: false },
  ];
}

/** The strip as one line of text, for its accessible label. */
export const usageText = (u: AgentUsage): string =>
  usageParts(u)
    .map((p) => (p.tight ? `${p.label}${p.value}` : `${p.label} ${p.value}`))
    .join(' ');
